import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { PageHeader, StatCard } from "@/components/common/DashboardBits";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MailCheck, MailWarning, Send, Loader2, RefreshCw, Landmark, Users, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "-");
const STATUS_CLASS = {
  sent: "bg-emerald-100 text-emerald-800 border-emerald-200",
  failed: "bg-red-100 text-red-800 border-red-200",
  skipped: "bg-amber-100 text-amber-800 border-amber-200",
};
const AUDIENCE_ICON = { admin: ShieldCheck, field: Users, bank: Landmark };

function ChannelCard({ status }) {
  if (!status) return null;
  const ok = status.configured;
  return (
    <Card className={`border ${ok ? "border-emerald-200 bg-emerald-50/40" : "border-amber-200 bg-amber-50/40"} mb-6`} data-testid="sendgrid-channel-card">
      <CardContent className="p-5 flex flex-col md:flex-row md:items-center gap-4">
        <div className={`h-11 w-11 rounded-md ${ok ? "bg-emerald-600" : "bg-amber-500"} text-white flex items-center justify-center flex-shrink-0`}>
          {ok ? <MailCheck className="h-5 w-5" /> : <MailWarning className="h-5 w-5" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xs font-bold uppercase tracking-widest text-slate-500">SendGrid channel</div>
          <div className="text-base font-semibold text-slate-900 mt-0.5" data-testid="sendgrid-status-text">
            {ok ? `Connected · sending as ${status.from_email}` : "Not connected — add your SendGrid credentials"}
          </div>
          {!ok && (
            <p className="text-sm text-slate-600 mt-1">
              Set <code className="font-mono text-xs bg-white border px-1 rounded">SENDGRID_API_KEY</code> (starts with <code className="font-mono text-xs">SG.</code>) and{" "}
              <code className="font-mono text-xs bg-white border px-1 rounded">SENDGRID_FROM_EMAIL</code> (a verified sender) in the backend environment, then restart. Until then events are logged as <em>skipped</em>.
            </p>
          )}
          {ok && status.sandbox && <p className="text-xs text-amber-700 mt-1">Sandbox mode is on — SendGrid validates but does not deliver.</p>}
        </div>
        <div className="text-xs text-slate-500 md:text-right whitespace-nowrap" data-testid="sendgrid-last-sent">Last delivered<br /><span className="text-slate-800 font-medium">{fmt(status.last_sent_at)}</span></div>
      </CardContent>
    </Card>
  );
}

function EventList({ events }) {
  return (
    <Card className="border border-slate-200" data-testid="notification-events-card">
      <CardContent className="p-5">
        <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">Automatic emails</div>
        <ul className="space-y-3">
          {events.map((e) => {
            const Icon = AUDIENCE_ICON[e.recipients.split(",")[0].trim()] || MailCheck;
            return (
              <li key={e.event} className="flex items-start gap-3" data-testid={`event-${e.event}`}>
                <div className="h-8 w-8 rounded-md bg-slate-950 text-white flex items-center justify-center flex-shrink-0"><Icon className="h-4 w-4" /></div>
                <div>
                  <div className="text-sm font-medium text-slate-900">{e.label}</div>
                  <div className="text-xs text-slate-500">To: {e.recipients}</div>
                </div>
              </li>
            );
          })}
        </ul>
      </CardContent>
    </Card>
  );
}

function TestEmailCard({ defaultTo, onSent }) {
  const [to, setTo] = useState(defaultTo || "");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  useEffect(() => { if (defaultTo && !to) setTo(defaultTo); }, [defaultTo, to]);
  const send = async () => {
    setBusy(true); setResult(null);
    try {
      const { data } = await api.post("/notifications/test", { to: to || undefined });
      setResult(data);
      toast[data.ok ? "success" : "error"](data.ok ? `Test email sent to ${data.to}` : data.error || "Send failed");
      onSent();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <Card className="border border-slate-200" data-testid="test-email-card">
      <CardContent className="p-5">
        <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">Send a test email</div>
        <div className="flex gap-2">
          <Input type="email" value={to} onChange={(e) => setTo(e.target.value)} placeholder="recipient@example.com" data-testid="test-email-input" />
          <Button onClick={send} disabled={busy || !to} className="bg-slate-950 hover:bg-slate-800" data-testid="send-test-email-button">
            {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />} Send
          </Button>
        </div>
        {result && (
          <div className={`mt-3 rounded-md border px-3 py-2 text-sm ${result.ok ? "border-emerald-200 bg-emerald-50 text-emerald-900" : "border-red-200 bg-red-50 text-red-800"}`} data-testid="test-email-result">
            {result.ok ? `Delivered to ${result.to} via SendGrid.` : result.error}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function NotificationsPage() {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [log, setLog] = useState({ items: [], total: 0 });
  const [filter, setFilter] = useState("all");

  const loadStatus = useCallback(() => api.get("/notifications/status").then((r) => setStatus(r.data)).catch((e) => toast.error(formatApiError(e))), []);
  const loadLog = useCallback(() =>
    api.get("/notifications", { params: { limit: 100, ...(filter !== "all" ? { status: filter } : {}) } }).then((r) => setLog(r.data)).catch((e) => toast.error(formatApiError(e))),
  [filter]);
  useEffect(() => { loadStatus(); }, [loadStatus]);
  useEffect(() => { loadLog(); }, [loadLog]);
  const refresh = () => { loadStatus(); loadLog(); };

  return (
    <div data-testid="notifications-page">
      <PageHeader title="Notifications" subtitle="SendGrid email alerts for Admin, Bank and Field Executive accounts"
        right={<Button variant="outline" onClick={refresh} data-testid="notifications-refresh"><RefreshCw className="h-4 w-4 mr-1" /> Refresh</Button>} />
      <ChannelCard status={status} />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <StatCard label="Sent" value={status?.counts?.sent ?? 0} tone="green" testId="notif-sent-count" />
        <StatCard label="Failed" value={status?.counts?.failed ?? 0} tone="red" testId="notif-failed-count" />
        <StatCard label="Skipped (not configured)" value={status?.counts?.skipped ?? 0} tone="amber" testId="notif-skipped-count" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <EventList events={status?.events || []} />
        <TestEmailCard defaultTo={user?.email} onSent={refresh} />
      </div>
      <div className="flex items-center gap-2 mb-4">
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-[220px]" data-testid="notif-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="sent">Sent</SelectItem>
            <SelectItem value="failed">Failed</SelectItem>
            <SelectItem value="skipped">Skipped</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-xs text-slate-500" data-testid="notif-total">{log.total} record(s)</span>
      </div>
      <Card className="border border-slate-200">
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Time</TableHead><TableHead>Event</TableHead><TableHead>To</TableHead><TableHead>Subject</TableHead><TableHead>Status</TableHead><TableHead>Error</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {log.items.map((n) => (
                <TableRow key={n.id} data-testid={`notif-row-${n.id}`}>
                  <TableCell className="text-xs text-slate-600 whitespace-nowrap">{fmt(n.created_at)}</TableCell>
                  <TableCell><span className="inline-flex px-2 py-0.5 rounded-md bg-slate-100 text-slate-800 text-xs font-medium">{n.event}</span></TableCell>
                  <TableCell className="text-xs">{n.to}</TableCell>
                  <TableCell className="text-xs text-slate-700 max-w-[320px] truncate">{n.subject}</TableCell>
                  <TableCell><span className={`inline-flex px-2 py-0.5 rounded-md border text-xs font-medium ${STATUS_CLASS[n.status] || STATUS_CLASS.skipped}`}>{n.status}</span></TableCell>
                  <TableCell className="text-xs text-slate-500 max-w-[280px] truncate">{n.error || "-"}</TableCell>
                </TableRow>
              ))}
              {log.items.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-slate-500 py-8">No notifications yet — they appear here when leads are created, assigned or visited.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
